import { OpraReportTemplate } from '@interfaces/report-templates.interface';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CustomReportTemplateFormInputs } from '@components/admin/add-custom-report-template-dialog/index';
import CharactersRemaining from '@components/core/characters-remaining';
import SelectWithCategories from '@components/core/select-with-categories';
import { SelectWithSearchDataArrayInterface } from '@components/core/select-with-categories/select-with-categories.interface';
import TextField from '@components/core/text-field';
import { useMap, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { validateFieldLength } from '@utils/validation';

type ModuleSpecificCheckboxName = keyof Pick<
    CustomReportTemplateFormInputs,
    'includeReviews' | 'includeInternalRisks' | 'includeNotApplicableRisks' | 'includeComparisonDataset'
>;
export type ModuleSpecificCheckboxesState = Record<ModuleSpecificCheckboxName, boolean>;

const FormFields = ({
    reportTemplatesNames,
    formatTypeOptions,
    moduleTypeOptions,
    powerBITemplateOptions,
    currentTemplate,
}: {
    reportTemplatesNames: string[];
    formatTypeOptions: SelectWithSearchDataArrayInterface[];
    moduleTypeOptions: SelectWithSearchDataArrayInterface[];
    powerBITemplateOptions: SelectWithSearchDataArrayInterface[];
    currentTemplate?: OpraReportTemplate | null;
}) => {
    const { t } = useTranslation();
    const {
        control,
        formState: { errors },
        watch,
        getValues,
        setValue,
    } = useFormContext<CustomReportTemplateFormInputs>();

    const moduleSpecificCheckboxesState = useMap<OpraReportTemplate['module'], ModuleSpecificCheckboxesState>();

    const moduleFieldValue = watch('module');
    const hasGeneratedReports = !!currentTemplate?.hasGeneratedReports;
    const hasErrorInFormatOrModule = !!errors.format?.message || !!errors.module?.message;

    const validateName = (name: string) => {
        const reportTemplatesNamesWithoutCurrent = reportTemplatesNames.filter(
            (tempName) => tempName !== currentTemplate?.templateName,
        );
        const isNameAlreadyExists = reportTemplatesNamesWithoutCurrent.includes(name.trim());
        if (isNameAlreadyExists) return t('admin:templateNameAlreadyExists');
    };

    const handleModuleChange = (onChange: (value: string) => void) => (event: DropDownListChangeEvent) => {
        const formValues = getValues();

        const getModule = (module?: number) => {
            if (module === Constants.Modules.CM2 || module === Constants.Modules.CM) {
                return Constants.Modules.CM;
            }
            if (module === Constants.Modules.RAM) {
                return Constants.Modules.RAM;
            }
            return undefined;
        };

        const prevModule = getModule(formValues.module as number | undefined);
        const rawSelectedModule = event.target.value as number | undefined;
        const selectedModule = getModule(rawSelectedModule);

        onChange(rawSelectedModule);

        const isValidModule = selectedModule === Constants.Modules.CM || selectedModule === Constants.Modules.RAM;
        if (!isValidModule || prevModule === selectedModule) return;

        // Save previous module checkbox states
        if (prevModule) {
            moduleSpecificCheckboxesState.set(prevModule, {
                includeReviews: formValues.includeReviews,
                includeInternalRisks: formValues.includeInternalRisks,
                includeNotApplicableRisks: formValues.includeNotApplicableRisks,
                includeComparisonDataset: formValues.includeComparisonDataset,
            });
        }

        const prevModuleState = moduleSpecificCheckboxesState.value.get(selectedModule);
        if (!prevModuleState) return;
        const isCMSelected = selectedModule === Constants.Modules.CM;
        setValue('includeReviews', isCMSelected ? false : prevModuleState.includeReviews);
        setValue('includeNotApplicableRisks', isCMSelected ? false : prevModuleState.includeNotApplicableRisks);
        setValue('includeInternalRisks', isCMSelected ? false : prevModuleState.includeInternalRisks);
        setValue('includeComparisonDataset', isCMSelected ? prevModuleState.includeComparisonDataset : false);
    };

    useEffect(() => {
        if (!currentTemplate?.module) return;
        moduleSpecificCheckboxesState.set(currentTemplate.module, {
            includeReviews: !!currentTemplate.includeReviews,
            includeInternalRisks: !!currentTemplate.includeInternalRisks,
            includeNotApplicableRisks: !!currentTemplate.includeNotApplicableRisks,
            includeComparisonDataset: !!currentTemplate.includeComparisonDataset,
        });
    }, []);

    return (
        <>
            <Controller
                control={control}
                name="inUse"
                render={({ field: { onChange, value } }) => (
                    <div className="checkboxes-wrapper !k-mr-5 !k-mb-3.5">
                        <div className="left-part">
                            <Checkbox
                                data-testid="inUse-checkbox"
                                rounded={null}
                                id="inUse-checkbox"
                                className="cell-checkbox no-border"
                                checked={value}
                                onChange={onChange}
                                label={t('admin:templateInUse')}
                                size="medium"
                            />
                        </div>
                    </div>
                )}
            />
            <div className={cn('grid-cols-2 type-edc-wrapper !k-mb-4', { '!k-mb-9': errors.templateName })}>
                <div className="form-control-type mr">
                    <Controller
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: t('ra:requiredFieldError'),
                            },
                        }}
                        name="pbiReportId"
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <SelectWithCategories
                                customClassName="form-control-type"
                                disabled={hasGeneratedReports}
                                fieldSelect="pbiReportId"
                                showLabels={false}
                                searchable
                                onChange={() => (event: DropDownListChangeEvent) => onChange(event.target.value)}
                                label={`${t('admin:powerBITemplate')}*`}
                                labelField="pbiReportId"
                                dataArray={powerBITemplateOptions}
                                customId="power-bi-template-select"
                                currentState={value}
                                error={error?.message}
                            />
                        )}
                    />
                </div>
                <Controller
                    control={control}
                    name="templateName"
                    rules={{
                        required: {
                            value: true,
                            message: t('ra:requiredFieldError'),
                        },
                        validate: {
                            maxLength: (value) => validateFieldLength(value, 'templateName', 200)!,
                            existName: (value) => validateName(value),
                        },
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <div className={cn('form-control-type')}>
                            <TextField
                                className="max-h-42 !k-mb-6.5"
                                disabled={hasGeneratedReports}
                                data-testId="report-template-name"
                                label={`${t('admin:templateName')}*`}
                                id="report-template-name"
                                fullWidth
                                value={value}
                                onChange={onChange}
                                placeholder={t('admin:enterTemplateName')}
                                helperText={
                                    <CharactersRemaining
                                        id="report-template-name-characters"
                                        length={value?.length}
                                        max={200}
                                        error={error?.message}
                                    />
                                }
                                variant="outlined"
                                color="secondary"
                                error={!!error?.message}
                            />
                        </div>
                    )}
                />
            </div>
            <div className={cn('type-edc-wrapper !k-mb-3', { '!k-mb-8': hasErrorInFormatOrModule })}>
                <Controller
                    control={control}
                    rules={{
                        required: {
                            value: true,
                            message: t('ra:requiredFieldError'),
                        },
                    }}
                    name="format"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <SelectWithCategories
                            disabled={hasGeneratedReports}
                            customId="format-select"
                            customClassName="form-control-type mr"
                            fieldSelect="format"
                            onChange={() => (event: DropDownListChangeEvent) => onChange(event.target.value)}
                            showLabels={false}
                            label={`${t('common:format')}*`}
                            labelField="format"
                            dataArray={formatTypeOptions}
                            currentState={value}
                            error={error?.message}
                        />
                    )}
                />
                <Controller
                    control={control}
                    rules={{
                        required: {
                            value: true,
                            message: t('ra:requiredFieldError'),
                        },
                    }}
                    name="module"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <SelectWithCategories
                            disabled={hasGeneratedReports}
                            customId="module-select"
                            customClassName="form-control-type"
                            fieldSelect="module"
                            showLabels={false}
                            onChange={() => handleModuleChange(onChange)}
                            label={`${t('common:module')}*`}
                            labelField="module"
                            dataArray={moduleTypeOptions}
                            currentState={value}
                            error={error?.message}
                        />
                    )}
                />
            </div>
            <div className={cn('type-edc-wrapper !k-mb-0.5', { '!k-mb-1.5': !!moduleFieldValue })}>
                <Controller
                    control={control}
                    name="includeSignatures"
                    render={({ field: { onChange, value } }) => (
                        <div className="checkboxes-wrapper !k-mr-5">
                            <div className="left-part">
                                <Checkbox
                                    data-testid="includeSignatures-checkbox"
                                    disabled={hasGeneratedReports}
                                    rounded={null}
                                    id="includeSignatures-checkbox"
                                    className="cell-checkbox no-border"
                                    checked={value}
                                    onChange={onChange}
                                    label={t('admin:includeSignatories')}
                                    labelClassName={cn({ 'checkbox-label-disabled': hasGeneratedReports })}
                                    size="medium"
                                />
                            </div>
                        </div>
                    )}
                />
                <Controller
                    control={control}
                    name="includeNameAndVersion"
                    render={({ field: { onChange, value } }) => (
                        <div className="checkboxes-wrapper">
                            <div className="left-part">
                                <Checkbox
                                    data-testid="includeNameAndVersion-checkbox"
                                    disabled={hasGeneratedReports}
                                    rounded={null}
                                    id="includeNameAndVersion-checkbox"
                                    className="cell-checkbox no-border"
                                    checked={value}
                                    onChange={onChange}
                                    label={t('admin:includeNameVersion')}
                                    labelClassName={cn({ 'checkbox-label-disabled': hasGeneratedReports })}
                                    size="medium"
                                />
                            </div>
                        </div>
                    )}
                />
            </div>
            {moduleFieldValue === Constants.Modules.RAM && (
                <div className="type-edc-wrapper !k-mb-1.5">
                    <Controller
                        control={control}
                        name="includeReviews"
                        render={({ field: { onChange, value } }) => (
                            <div className="checkboxes-wrapper !k-mr-5">
                                <div className="left-part">
                                    <Checkbox
                                        data-testid="includeReviews-checkbox"
                                        disabled={hasGeneratedReports}
                                        rounded={null}
                                        id="includeReviews-checkbox"
                                        className="cell-checkbox no-border"
                                        checked={value}
                                        onChange={onChange}
                                        label={t('admin:includeReview')}
                                        size="medium"
                                    />
                                </div>
                            </div>
                        )}
                    />
                    <Controller
                        control={control}
                        name="includeNotApplicableRisks"
                        render={({ field: { onChange, value } }) => (
                            <div className="checkboxes-wrapper">
                                <div className="left-part">
                                    <Checkbox
                                        data-testid="includeNotApplicableRisks-checkbox"
                                        disabled={hasGeneratedReports}
                                        rounded={null}
                                        id="includeNotApplicableRisks-checkbox"
                                        className="cell-checkbox no-border"
                                        checked={value}
                                        onChange={onChange}
                                        label={t('admin:includeNotApplicableRisks')}
                                        size="medium"
                                    />
                                </div>
                            </div>
                        )}
                    />
                </div>
            )}
            <div className="type-edc-wrapper !k-mb-3">
                {moduleFieldValue === Constants.Modules.RAM && (
                    <Controller
                        control={control}
                        name="includeInternalRisks"
                        render={({ field: { onChange, value } }) => (
                            <div className="checkboxes-wrapper">
                                <div className="left-part">
                                    <Checkbox
                                        data-testid="includeInternalRisks-checkbox"
                                        disabled={hasGeneratedReports}
                                        rounded={null}
                                        id="includeInternalRisks-checkbox"
                                        className="cell-checkbox no-border"
                                        checked={value}
                                        onChange={onChange}
                                        label={t('admin:includeInternalRisks')}
                                        size="medium"
                                    />
                                </div>
                            </div>
                        )}
                    />
                )}
                {(moduleFieldValue === Constants.Modules.CM || moduleFieldValue === Constants.Modules.CM2) && (
                    <Controller
                        control={control}
                        name="includeComparisonDataset"
                        render={({ field: { onChange, value } }) => (
                            <div className="checkboxes-wrapper">
                                <div className="left-part">
                                    <Checkbox
                                        data-testid="includeComparisonDataset-checkbox"
                                        disabled={hasGeneratedReports}
                                        rounded={null}
                                        id="includeComparisonDataset-checkbox"
                                        className="cell-checkbox no-border"
                                        checked={value}
                                        onChange={onChange}
                                        label={t('admin:includeComparisonDataset')}
                                        labelClassName={cn({ 'checkbox-label-disabled': hasGeneratedReports })}
                                        size="medium"
                                    />
                                </div>
                            </div>
                        )}
                    />
                )}
            </div>
            <div className="type-edc-wrapper !k-mb-1 !k-mt-2.5">
                <Controller
                    control={control}
                    name="description"
                    rules={{
                        validate: {
                            maxLength: (value) => validateFieldLength(value, 'description', 500)!,
                        },
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <TextField
                            data-testId="report-template-description"
                            label={`${t('description')}`}
                            id="report-template-description"
                            multiline
                            rows={5}
                            fullWidth
                            value={value}
                            onChange={onChange}
                            placeholder={t('admin:enterDescription')}
                            helperText={
                                <CharactersRemaining
                                    id="report-template-description-сharacters"
                                    length={value?.length}
                                    max={500}
                                    error={error?.message}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!error?.message}
                            className="before-select"
                        />
                    )}
                />
            </div>
        </>
    );
};

export default FormFields;
